import { Alert, Grid, IconButton, Link, Snackbar, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentCopy, Edit, WhatsApp } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { LinksService, QrBoardDTO } from '../api';
import { CellProps, useTable } from '../table/useTable';
import { EditQrBoardDrawer } from './EditQrBoardDrawer';
import { useSafeExecute } from '../common/apiNotifications/safeExecute';
import { mb } from '../common/theme/mbTokens';

const getQrRedirectUrl = (shortCode: string) => `${import.meta.env.VITE_REACT_APP_LINKS_HOST}/${shortCode}`;

const qrBoardsFetcher = async ({ search, page, perPage }: { search?: string; page: number; perPage: number }) =>
  LinksService.listQrBoards(page, perPage, search);

export const QrBoardsPage = React.memo(() => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedQrBoard, setSelectedQrBoard] = useState<QrBoardDTO | undefined>();
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const safeExecute = useSafeExecute();
  const posthog = usePostHog();

  const handleCopy = async (shortCode: string) => {
    await navigator.clipboard.writeText(getQrRedirectUrl(shortCode));
    setCopySnackbarOpen(true);
  };

  const openDrawer = (qrBoard: QrBoardDTO) => {
    setSelectedQrBoard(qrBoard);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedQrBoard(undefined);
  };

  const tableDetails: CellProps<QrBoardDTO>[] = [
    {
      id: 'edit',
      alignment: 'center',
      width: 50,
      render: (qr) => (
        <IconButton onClick={() => openDrawer(qr)}>
          <Edit />
        </IconButton>
      ),
    },
    {
      id: 'label',
      alignment: 'center',
      label: t('qrBoards.label'),
      render: (qr) => qr.label || '-',
    },
    {
      id: 'short_code',
      alignment: 'center',
      label: t('qrBoards.shortCode'),
      render: (qr) => qr.short_code,
    },
    {
      id: 'long_link',
      alignment: 'center',
      label: t('qrBoards.destinationUrl'),
      render: (qr) => <Typography sx={{ textTransform: 'lowercase' }}>{qr.long_link || '-'}</Typography>,
    },
    {
      id: 'copy',
      alignment: 'center',
      width: 50,
      render: (qr) => (
        <IconButton onClick={() => handleCopy(qr.short_code)}>
          <ContentCopy />
        </IconButton>
      ),
    },
  ];

  const { TableComponent, mutate } = useTable<QrBoardDTO>({
    opId: 'listQrBoards',
    fetcher: qrBoardsFetcher,
    tableDetails,
    fillHeight: true,
  });

  useEffect(() => {
    const getQr = async () => {
      const code = searchParams.get('code');
      if (code) {
        setSearchParams({}, { replace: true });
        try {
          const qrBoard = await safeExecute({
            apiCall: () => LinksService.getQrBoard({ short_code: code }),
            successMessage: '',
            suppressSuccessMessage: true,
            onErrorMessageGenerator: (error) =>
              error.status === 404 ? t('qrBoards.notFound') : error.body?.message || t('api.error.default'),
          });
          setSelectedQrBoard(qrBoard);
          setDrawerOpen(true);
          posthog.capture('QR board scanned', { shortCode: code });
        } catch {
          // Error already handled by safeExecute
        }
      }
    };
    void getQr();
  }, [searchParams, setSearchParams, safeExecute, t]);

  const handleUpdate = () => {
    void mutate();
  };

  const whatsappMessage = encodeURIComponent(t('qrBoards.whatsappMessage'));
  const whatsappLink = `https://wa.me/972526149234?text=${whatsappMessage}`;

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0 }}>
      <Grid container flexDirection="column" sx={{ px: mb.layout.cardPadding, pt: mb.layout.cardPadding, pb: 2 }}>
        <Typography variant="h5" gutterBottom>
          {t('qrBoards.pageTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>
          {t('qrBoards.pageSubtitle')}
        </Typography>
        <Link
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 'fit-content' }}>
          <WhatsApp fontSize="small" color="primary" />
          <Typography variant="body2" color="primary.main">
            {t('qrBoards.orderMore')}
          </Typography>
        </Link>
      </Grid>
      {TableComponent}
      <EditQrBoardDrawer open={drawerOpen} onClose={closeDrawer} qrBoard={selectedQrBoard} onUpdate={handleUpdate} />
      <Snackbar
        open={copySnackbarOpen}
        onClose={() => setCopySnackbarOpen(false)}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setCopySnackbarOpen(false)} severity="success">
          {t('copiedToClipboard')}
        </Alert>
      </Snackbar>
    </Grid>
  );
});
